import { useState, useEffect } from 'react';
import Sidebar from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Plus, Shield, Trash2, Edit2, Users } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PERMISSION_LABELS = {
  view_assigned_events: { label: 'View Assigned Events', description: 'Can view events they are assigned to' },
  view_all_events: { label: 'View All Events', description: 'Can view all bookings and events' },
  accept_payments: { label: 'Accept Payments', description: 'Can record and manage payments' },
  add_leads: { label: 'Add Leads', description: 'Can create new leads' },
  add_bookings: { label: 'Add Bookings', description: 'Can create new bookings' },
  manage_employees: { label: 'Manage Employees', description: 'Can add, edit, and delete employees' },
  access_settings: { label: 'Access Settings', description: 'Can view and modify company settings' },
  access_configuration: { label: 'Access Configuration', description: 'Can access packages, templates, forms, etc.' },
  view_accounting: { label: 'View Accounting', description: 'Can view financial reports and accounting' },
  administrator: { label: 'Administrator', description: 'Full access to all features (overrides other permissions)' }
};

const ROLE_COLORS = [
  { value: '#ef4444', label: 'Red' },
  { value: '#f97316', label: 'Orange' },
  { value: '#eab308', label: 'Yellow' },
  { value: '#22c55e', label: 'Green' },
  { value: '#06b6d4', label: 'Cyan' },
  { value: '#3b82f6', label: 'Blue' },
  { value: '#8b5cf6', label: 'Purple' },
  { value: '#ec4899', label: 'Pink' },
  { value: '#64748b', label: 'Slate' }
];

export default function RolesPage({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [newRole, setNewRole] = useState({
    name: '',
    description: '',
    color: '#06b6d4',
    permissions: {
      view_assigned_events: false,
      view_all_events: false,
      accept_payments: false,
      add_leads: false,
      add_bookings: false,
      manage_employees: false,
      access_settings: false,
      access_configuration: false,
      view_accounting: false,
      administrator: false
    }
  });

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/roles`);
      setRoles(response.data);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setNewRole({
      name: '',
      description: '',
      color: '#06b6d4',
      permissions: {
        view_assigned_events: false,
        view_all_events: false,
        accept_payments: false,
        add_leads: false,
        add_bookings: false,
        manage_employees: false,
        access_settings: false,
        access_configuration: false,
        view_accounting: false,
        administrator: false
      }
    });
    setEditingRole(null);
  };

  const openEditDialog = (role) => {
    setEditingRole(role);
    setNewRole({
      name: role.name,
      description: role.description || '',
      color: role.color || '#06b6d4',
      permissions: role.permissions || {}
    });
    setShowDialog(true);
  };

  const handleCreate = async () => {
    if (!newRole.name.trim()) {
      toast.error('Please enter a role name');
      return;
    }

    try {
      if (editingRole) {
        await axios.put(`${API}/roles/${editingRole.id}`, newRole);
        toast.success('Role updated successfully');
      } else {
        await axios.post(`${API}/roles`, newRole);
        toast.success('Role created successfully');
      }
      setShowDialog(false);
      resetForm();
      fetchRoles();
    } catch (error) {
      toast.error(editingRole ? 'Failed to update role' : 'Failed to create role');
    }
  };

  const handleDelete = async (roleId, roleName, isSystem) => {
    if (isSystem) {
      toast.error('Cannot delete system roles');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete the "${roleName}" role?`)) return;

    try {
      await axios.delete(`${API}/roles/${roleId}`);
      toast.success('Role deleted');
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    }
  };

  const togglePermission = (permission) => {
    setNewRole({
      ...newRole,
      permissions: {
        ...newRole.permissions,
        [permission]: !newRole.permissions[permission]
      }
    });
  };

  const countPermissions = (permissions) => {
    if (!permissions) return 0;
    return Object.values(permissions).filter(v => v === true).length;
  };

  return (
    <div className="flex min-h-screen bg-obsidian" data-testid="roles-page">
      <Sidebar user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} />
      
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="font-heading text-4xl font-bold mb-2" data-testid="roles-heading">Roles & Permissions</h1>
              <p className="text-muted-foreground">Manage employee roles and access levels</p>
            </div>
            <Dialog open={showDialog} onOpenChange={(open) => {
              setShowDialog(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-neon-blue hover:bg-neon-blue/80 text-black" data-testid="create-role-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Role
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="role-dialog">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl text-foreground">
                    {editingRole ? 'Edit Role' : 'Create New Role'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-6 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-foreground">Role Name *</Label>
                      <Input
                        value={newRole.name}
                        onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                        placeholder="e.g., Manager, Technician"
                        className="bg-background border-border text-foreground"
                        data-testid="role-name-input"
                        disabled={editingRole?.is_system}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-foreground">Color</Label>
                      <div className="flex gap-2 flex-wrap">
                        {ROLE_COLORS.map((color) => (
                          <button
                            key={color.value}
                            type="button"
                            onClick={() => setNewRole({ ...newRole, color: color.value })}
                            className={`w-8 h-8 rounded-full border-2 transition-all ${
                              newRole.color === color.value ? 'border-white scale-110' : 'border-transparent'
                            }`}
                            style={{ backgroundColor: color.value }}
                            title={color.label}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-foreground">Description</Label>
                    <Input
                      value={newRole.description}
                      onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                      placeholder="Brief description of this role"
                      className="bg-background border-border text-foreground"
                      data-testid="role-description-input"
                    />
                  </div>

                  <div className="space-y-4">
                    <Label className="text-foreground text-lg">Permissions</Label>
                    <div className="space-y-3">
                      {Object.entries(PERMISSION_LABELS).map(([key, { label, description }]) => (
                        <div 
                          key={key}
                          className={`flex items-center justify-between p-4 rounded-xl border transition-colors ${
                            newRole.permissions[key] 
                              ? 'border-primary bg-primary/10' 
                              : 'border-border hover:bg-muted/30'
                          }`}
                        >
                          <div className="flex-1">
                            <div className="font-medium text-foreground">{label}</div>
                            <div className="text-xs text-muted-foreground">{description}</div>
                          </div>
                          <Switch
                            checked={newRole.permissions[key] || false}
                            onCheckedChange={() => togglePermission(key)}
                            data-testid={`permission-${key}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowDialog(false);
                        resetForm();
                      }}
                      className="border-border"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleCreate}
                      className="bg-neon-blue hover:bg-neon-blue/80 text-black"
                      data-testid="save-role-btn"
                    >
                      {editingRole ? 'Update Role' : 'Create Role'}
                    </Button>
                  </DialogFooter>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : roles.length === 0 ? (
            <div className="text-center py-12 glass-card rounded-3xl" data-testid="no-roles-message">
              <Shield className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No roles yet. Create your first role!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="roles-list">
              {roles.map((role) => (
                <div
                  key={role.id}
                  className="glass-card-hover rounded-2xl p-6"
                  data-testid={`role-item-${role.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: `${role.color}20` }}
                      >
                        <Shield className="w-5 h-5" style={{ color: role.color }} />
                      </div>
                      <div>
                        <h3 className="font-heading text-xl font-semibold">{role.name}</h3>
                        {role.is_system && (
                          <span className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground">
                            System
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEditDialog(role)}
                        className="text-muted-foreground hover:text-foreground"
                        data-testid={`edit-role-btn-${role.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      {!role.is_system && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(role.id, role.name, role.is_system)}
                          className="text-red-500 hover:text-red-400"
                          data-testid={`delete-role-btn-${role.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {role.description && (
                    <p className="text-sm text-muted-foreground mb-4">{role.description}</p>
                  )}
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Permissions</span>
                      <span className="font-medium" style={{ color: role.color }}>
                        {countPermissions(role.permissions)} / {Object.keys(PERMISSION_LABELS).length}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {role.permissions && Object.entries(role.permissions)
                        .filter(([_, value]) => value)
                        .slice(0, 4)
                        .map(([key]) => (
                          <span 
                            key={key}
                            className="text-xs px-2 py-1 rounded-full"
                            style={{ backgroundColor: `${role.color}20`, color: role.color }}
                          >
                            {PERMISSION_LABELS[key]?.label || key}
                          </span>
                        ))
                      }
                      {role.permissions && Object.values(role.permissions).filter(v => v).length > 4 && (
                        <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                          +{Object.values(role.permissions).filter(v => v).length - 4} more
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
